// Smart Ranking Engine (transitivity-aware + topo sort + strong tiebreakers)
// Identity should be unique IDs (strings). Titles are for display only.

class SmartRanker {
  constructor(items) {
    this.items = Array.from(items ?? []);
    this.results = Object.create(null);   // winner -> Set(losers)
    this.comparisons = new Set();         // "a|b"
    this.index = new Map(this.items.map((x, i) => [x, i])); // stable fallback
  }

  _ensureSet(k) {
    if (!this.results[k]) this.results[k] = new Set();
    return this.results[k];
  }

  recordResult(winner, loser) {
    if (!winner || !loser || winner === loser) return;
    this._ensureSet(winner).add(loser);
    this.comparisons.add(`${winner}|${loser}`);
  }

  // Reachability by DFS (inferred edge)
  inferredWin(winner, loser) {
    if (winner === loser) return false;
    const visited = new Set();
    const stack = [winner];
    while (stack.length) {
      const cur = stack.pop();
      if (cur === loser) return true;
      const outs = this.results[cur];
      if (!outs) continue;
      for (const opp of outs) {
        if (!visited.has(opp)) {
          visited.add(opp);
          stack.push(opp);
        }
      }
    }
    return false;
  }

  _winCounts() {
    const counts = new Map(this.items.map(i => [i, 0]));
    for (const w in this.results) {
      counts.set(w, (counts.get(w) || 0) + (this.results[w]?.size || 0));
    }
    return counts;
  }

  // Ask only for necessary pairs; if everything is inferred but ties remain,
  // probe within tied buckets to resolve ambiguity.
  nextPair() {
    // Pass 1: standard — not compared, not transitively decided
    for (let i = 0; i < this.items.length; i++) {
      for (let j = i + 1; j < this.items.length; j++) {
        const a = this.items[i], b = this.items[j];
        if (this.comparisons.has(`${a}|${b}`) || this.comparisons.has(`${b}|${a}`)) continue;
        if (this.inferredWin(a, b) || this.inferredWin(b, a)) continue;
        return [a, b];
      }
    }

    // Pass 2: probe inside tied buckets
    const wins = this._winCounts();
    const buckets = new Map(); // wins -> ids[]
    for (const it of this.items) {
      const w = wins.get(it) || 0;
      if (!buckets.has(w)) buckets.set(w, []);
      buckets.get(w).push(it);
    }
    for (const group of buckets.values()) {
      if (group.length < 2) continue;
      for (let i = 0; i < group.length; i++) {
        for (let j = i + 1; j < group.length; j++) {
          const a = group[i], b = group[j];
          if (this.comparisons.has(`${a}|${b}`) || this.comparisons.has(`${b}|${a}`)) continue;
          const aBeatsB = this.inferredWin(a, b);
          const bBeatsA = this.inferredWin(b, a);
          if (!aBeatsB && !bBeatsA) return [a, b];
        }
      }
    }

    return null;
  }

  _graph() {
    const adj = new Map(this.items.map(i => [i, new Set()]));
    const indeg = new Map(this.items.map(i => [i, 0]));
    for (const w in this.results) {
      for (const l of this.results[w]) {
        if (!adj.get(w).has(l)) {
          adj.get(w).add(l);
          indeg.set(l, (indeg.get(l) || 0) + 1);
        }
      }
    }
    return { adj, indeg };
  }

  // Comparator: DIRECT head-to-head > inferred > wins > original index
  _cmp(a, b, wins) {
    const directAB = !!this.results[a]?.has(b);
    const directBA = !!this.results[b]?.has(a);
    if (directAB && !directBA) return -1;
    if (directBA && !directAB) return 1;

    const infAB = this.inferredWin(a, b);
    const infBA = this.inferredWin(b, a);
    if (infAB && !infBA) return -1;
    if (infBA && !infAB) return 1;

    const dw = (wins.get(b) || 0) - (wins.get(a) || 0);
    if (dw !== 0) return dw;

    return (this.index.get(a) || 0) - (this.index.get(b) || 0);
  }

  // Topo sort with strong tie-breaking
  generateRanking() {
    const { adj, indeg } = this._graph();
    const wins = this._winCounts();

    let queue = this.items.filter(i => (indeg.get(i) || 0) === 0);
    queue.sort((a, b) => this._cmp(a, b, wins));

    const order = [];
    const indegMut = new Map(indeg);

    while (queue.length) {
      const cur = queue.shift();
      order.push(cur);

      for (const nb of adj.get(cur) || []) {
        indegMut.set(nb, (indegMut.get(nb) || 0) - 1);
        if (indegMut.get(nb) === 0) queue.push(nb);
      }
      queue.sort((a, b) => this._cmp(a, b, wins));
    }

    if (order.length < this.items.length) {
      const remaining = this.items.filter(i => !order.includes(i));
      remaining.sort((a, b) => this._cmp(a, b, wins));
      order.push(...remaining);
    }

    return order;
  }
}

export default SmartRanker;
